import * as React from "react";
import axios from "axios";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import IconButton from "@mui/joy/IconButton";
import Menu from "@mui/joy/Menu";
import MenuItem from "@mui/joy/MenuItem";
import FormatBold from "@mui/icons-material/FormatBold";
import FormatItalic from "@mui/icons-material/FormatItalic";
import FormatUnderlined from "@mui/icons-material/FormatUnderlined";
import TextFields from "@mui/icons-material/TextFields";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import "./CsmNotes.css";
import { useParams } from "react-router-dom";
import { API_URL } from "../../utils/auth";
import Auth from "../../utils/auth";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./quill-fonts.css";

const Font = Quill.import("formats/font");
Font.whitelist = ['arial', 'courier-new', 'georgia', 'times-new-roman', 'verdana'];
Quill.register(Font, true);

export default function ExampleTextareaComment() {
  const [notes, setNotes] = React.useState({
    actionItems: "",
    clientJourney: "",
  });
  const [loading, setLoading] = React.useState({ actionItems: false, clientJourney: false });
  const [hasChanges, setHasChanges] = React.useState({ actionItems: false, clientJourney: false });
  const fontOptions = [
    { name: "Arial", value: "arial" },
    { name: "Courier New", value: "courier-new" },
    { name: "Georgia", value: "georgia" },
    { name: "Times New Roman", value: "times-new-roman" },
    { name: "Verdana", value: "verdana" }
  ];
  const [fontMenuAnchor, setFontMenuAnchor] = React.useState(null);
  const [activeField, setActiveField] = React.useState(null);
  const { clientId } = useParams();
  const workspaceId = Auth.getWorkspaceId();
  const [noteIds, setNoteIds] = React.useState({
    actionItems: null,
    clientJourney: null,
  });

  const editorRefs = {
    actionItems: React.useRef(null),
    clientJourney: React.useRef(null),
  };

  const quillInstances = React.useRef({});
  const initialContent = React.useRef({
    actionItems: "",
    clientJourney: ""
  });

  const fetchData = async () => {
    try {
      if (!clientId || !workspaceId) {
        throw new Error("Missing clientId or workspaceId parameter.");
      }
      
      const notesUrl = `${API_URL}/utils/notes?clientId=${clientId}&workspaceId=${workspaceId}`;
      const notesResponse = await axios.get(notesUrl);
      const actionItemsData = notesResponse.data?.[0];
      
      const journeyUrl = `${API_URL}/utils/journey?clientId=${clientId}&workspaceId=${workspaceId}`;
      const journeyResponse = await axios.get(journeyUrl);
      const journeyData = journeyResponse.data?.[0];

      const newNotes = {
        actionItems: actionItemsData?.content || "",
        clientJourney: journeyData?.content || "",
      };

      setNotes(newNotes);
      initialContent.current = {...newNotes};

      setNoteIds({
        actionItems: actionItemsData?.id || null,
        clientJourney: journeyData?.id || null,
      });
    } catch (error) {
      console.error("Error in fetchData:", error.response?.data || error.message);
    }
  };

  const sendData = async (field) => {
    try {
      if (!clientId) {
        throw new Error("Client ID is required");
      }

      const noteId = noteIds[field];
      const noteContent = notes[field] || "";
      const endpoint = field === "clientJourney" ? "journey" : "notes";

      // Validar contenido
      const isEmpty = !noteContent || noteContent === "<p><br></p>" || noteContent.trim() === "";
      if (isEmpty) {
        alert("Please enter some content before saving");
        return;
      }

      const payload = {
        ...(noteId && { id: noteId }),
        content: noteContent,
        clientId: parseInt(clientId),  
        workspaceId: parseInt(workspaceId),
        updatedAt: new Date().toISOString()
      };

      console.log("Sending payload:", payload); 
      if (!payload.clientId || !payload.content) {
        throw new Error("Missing required fields: clientId or content");
      }

      setLoading((prev) => ({ ...prev, [field]: true }));

      const url = `${API_URL}/utils/${endpoint}`;
      const response = noteId 
        ? await axios.put(url, payload) 
        : await axios.post(url, payload);

      if (!noteId) {
        setNoteIds(prev => ({ ...prev, [field]: response.data.id }));
      }

      setHasChanges(prev => ({ ...prev, [field]: false }));
      initialContent.current[field] = noteContent;

    } catch (error) {
      console.error(`Error updating ${field}:`, error.response?.data || error.message);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSend = (field) => {
    sendData(field);
  };

  const openFontMenu = (event, field) => {
    if (fontMenuAnchor && activeField === field) {
      closeFontMenu();
    } else {
      setFontMenuAnchor(event.currentTarget);
      setActiveField(field);
    }
  };

  const closeFontMenu = () => {
    setFontMenuAnchor(null);
    setActiveField(null);
  };

  const applyStyle = (field, command, value = null) => {
    const quillInstance = quillInstances.current[field];
    if (quillInstance) {
      quillInstance.focus();
      const selection = quillInstance.getSelection();
      if (selection && selection.length > 0) {
        if (command === "font") {
          quillInstance.format("font", value);
        } else {
          const currentFormat = quillInstance.getFormat(selection);
          quillInstance.format(command, !currentFormat[command]);
        }
      }
    }
  };

  React.useEffect(() => {
    Object.keys(editorRefs).forEach((field) => {
      if (editorRefs[field].current && !quillInstances.current[field]) {
        const quillInstance = new Quill(editorRefs[field].current, {
          theme: "snow",
          modules: {
            toolbar: false,
          },
        });

        quillInstances.current[field] = quillInstance;

        quillInstance.on("text-change", () => {
          const content = quillInstance.root.innerHTML;
          setNotes((prev) => ({
            ...prev,
            [field]: content,
          }));
          
          setHasChanges(prev => ({
            ...prev,
            [field]: content !== initialContent.current[field]
          }));
        });
      }
    });

    return () => {
      Object.keys(quillInstances.current).forEach((field) => {
        if (quillInstances.current[field]) {
          quillInstances.current[field] = null;
        }
      });
    };
  }, []);

  React.useEffect(() => {
    Object.keys(editorRefs).forEach((field) => {
      if (quillInstances.current[field]) {
        if (quillInstances.current[field].root.innerHTML !== notes[field]) {
          quillInstances.current[field].root.innerHTML = notes[field] || "";
        }
      }
    });
  }, [notes]);

  React.useEffect(() => {
    if (clientId && workspaceId) {
      fetchData();
    } else {
      console.error("Missing clientId or workspaceId parameter.");
    }
  }, [clientId, workspaceId]);

  return (
    <Box className="card-container">
      {[
        {
          field: "actionItems",
          label: "CSM Notes",
        },
        {
          field: "clientJourney",
          label: "Client Journey",
        },
      ].map(({ field, label }, index) => (
        <FormControl className="comment-box" key={index}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2,
            }}
          >
            <FormLabel
              sx={{
                fontFamily: "Albert Sans",
                fontSize: "1.5rem",
                fontWeight: "bold",
                color: "#333",
              }}
            >
              {label}
            </FormLabel>
            <Button
              className="send-button"
              onClick={() => handleSend(field)}
              disabled={!clientId || loading[field] || !hasChanges[field]}
              color={hasChanges[field] ? "primary" : "neutral"}
            >
              {loading[field] ? "Saving..." : "Save"}
            </Button>
          </Box>
          <Box className="toolbar" sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={() => applyStyle(field, "bold")}
              sx={{ color: "#333" }}
            >
              <FormatBold />
            </IconButton>
            <IconButton
              onClick={() => applyStyle(field, "italic")}
              sx={{ color: "#333" }}
            >
              <FormatItalic />
            </IconButton>
            <IconButton
              onClick={() => applyStyle(field, "underline")}
              sx={{ color: "#333" }}
            >
              <FormatUnderlined />
            </IconButton>
            <IconButton onClick={(event) => openFontMenu(event, field)}>
              <TextFields />
              <KeyboardArrowDown />
            </IconButton>
            <Menu
              anchorEl={fontMenuAnchor}
              open={Boolean(fontMenuAnchor) && activeField === field}
              onClose={closeFontMenu}
            >
              {fontOptions.map((font) => (
                <MenuItem
                  key={font.value}
                  onClick={() => {
                    applyStyle(field, "font", font.value);
                    closeFontMenu();
                  }}
                  style={{ fontFamily: font.name }}
                >
                  {font.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          {/* Quill Editor */}
          <div
            ref={editorRefs[field]}
            style={{
              width: "100%",
              minHeight: "150px",
              maxHeight: "200px",
              border: "1px solid #ddd",
              borderRadius: "20px",
              padding: "8px",
              overflowY: "auto",
            }}
          />
        </FormControl>
      ))}
    </Box>
  );
}